import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import preferenciaModalService from "services/PreferenciaModalService";

/**
 * Provider de preferências de modais (one-time / por-versão / snooze). Faz UM GET em lote no mount e
 * compartilha o resultado; cada modal consome sua fatia via useModalDismissal.
 *
 * Filosofia: o back decide o que exibir (versão / snooze / deadline). O front é
 * burro — só renderiza o que vem e avisa o dismissal. O LAYOUT e o TEXTO de cada
 * modal continuam no componente; o servidor nunca manda layout.
 */
const PreferenciasModaisContext = createContext({
	carregando: true,
	modais: [],
	dispensar: async () => {},
});

export const PreferenciasModaisProvider = ({ children }) => {
	const [carregando, setCarregando] = useState(true);
	const [modais, setModais] = useState([]);

	// Espelho do estado atual para o dispensar não fechar sobre valor stale.
	const modaisRef = useRef([]);
	modaisRef.current = modais;

	useEffect(() => {
		let ativo = true;

		preferenciaModalService
			.getModaisParaExibir()
			.then((resp) => {
				if (ativo) setModais(resp?.modais || []);
			})
			.catch(() => {
				// fail-closed: sem resposta da API, não exibe nada (melhor não
				// reabrir um aviso já fechado do que incomodar). O no-notification
				// no service evita o toast de erro.
				if (ativo) setModais([]);
			})
			.finally(() => {
				if (ativo) setCarregando(false);
			});

		return () => {
			ativo = false;
		};
	}, []);

	const dispensar = useCallback(async (chave, acao) => {
		const item = modaisRef.current.find((m) => m.chave === chave);
		if (!item) return;

		const { versao } = item;

		// Esconde otimista: não trava o usuário esperando o POST.
		setModais((prev) => prev.filter((m) => m.chave !== chave));

		try {
			await preferenciaModalService.dispensarModal(chave, versao, acao);
		} catch (e) {
			// Já escondeu nesta sessão. Se o POST falhou (offline), o modal
			// reaparece no próximo load — tradeoff aceito para estes avisos.
		}
	}, []);

	return (
		<PreferenciasModaisContext.Provider
			value={{ carregando, modais, dispensar }}
		>
			{children}
		</PreferenciasModaisContext.Provider>
	);
};

/**
 * Hook por modal. Retorna se deve exibir, a versão vigente, o payload opcional
 * (conteúdo server-driven, null para modais com layout/texto no componente) e
 * uma função dispensar(acao) com hide otimista.
 */
export const useModalDismissal = (chave) => {
	const { carregando, modais, dispensar } = useContext(
		PreferenciasModaisContext,
	);

	const item = modais.find((m) => m.chave === chave);

	return {
		carregando,
		deveExibir: !carregando && !!item,
		versao: item?.versao,
		payload: item?.payload || null,
		dispensar: useCallback((acao) => dispensar(chave, acao), [dispensar, chave]),
	};
};

export default PreferenciasModaisProvider;
